/**
 * @fileoverview Protected Route Component
 * @description Ensures only authenticated users can access routes.
 * Optionally restricts by role or permission.
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePermissions, type UserPermissions } from '@/hooks/usePermissions';
import { Loader2, ShieldAlert } from 'lucide-react';
import type { AppRole } from '@/types/database';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Require one of these roles */
  roles?: AppRole[];
  /** Require this permission */
  permission?: keyof UserPermissions;
}

export function ProtectedRoute({ children, roles, permission }: ProtectedRouteProps) {
  const { user, isLoading, role } = useAuthContext();
  const { can, isLoading: permLoading } = usePermissions();

  if (isLoading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Role check
  if (roles && role && !roles.includes(role) && role !== 'super_admin') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <ShieldAlert className="h-16 w-16 text-destructive" />
        <h1 className="text-xl font-semibold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  // Permission check
  if (permission && !can(permission)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <ShieldAlert className="h-16 w-16 text-destructive" />
        <h1 className="text-xl font-semibold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground">You don't have the required permission.</p>
      </div>
    );
  }

  return <>{children}</>;
}
